import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Card,
  CircularProgress,
  Fade,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import useFadeOpen from './useFadeOpen';

type PropType = {
  open?: boolean;
  onClose?: () => void;
  onClickPayBulkSkip: () => void;
  sectionArray: {
    title: string;
    info: React.ReactNode;
  }[];
  isChecked: boolean;
  handleCheckboxChange: () => void;
  isSubmiting?: boolean;
};

const BillModal: React.FC<PropType> = ({
  open,
  onClose,
  onClickPayBulkSkip,
  sectionArray,
  isChecked,
  isSubmiting,
  handleCheckboxChange,
}) => {
  const isOpen = open || false;
  const showContent = useFadeOpen(isOpen);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          timeout: 400,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.45)' },
        },
      }}
    >
      <Fade in={showContent} timeout={400}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 32px)',
            maxWidth: '450px',
            outline: 'none',
          }}
        >
          <Card
            sx={{
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 4,
              p: 3,
              boxShadow: 24,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              mb={2}
              pb={1}
              sx={{
                borderBottom: '1px solid',
                borderBottomColor: 'secondary.main',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: '24px',
                  fontWeight: 900,
                  lineHeight: '36px',
                  letterSpacing: '0.03em',
                  textAlign: 'left',
                }}
              >
                CONFIRM
              </Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              {sectionArray.map((section, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 'bold', mr: 1, maxWidth: '60%' }}
                  >
                    {section.title}
                  </Typography>
                  {section.info}
                </Box>
              ))}
            </Box>

            <FormControlLabel
              control={(
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              )}
              label={(
                <Typography variant="body2" sx={{ color: '#222222' }}>
                  I have read and agreed with the{' '}
                  <Link
                    href="/fulfillment"
                    target="_blank"
                    color="primary"
                  >
                    Fulfillment Policy
                  </Link>
                </Typography>
              )}
              sx={{ my: 3 }}
            />

            <Button
              variant="contained"
              startIcon={isSubmiting && <CircularProgress size={20} />}
              disabled={!isChecked || isSubmiting}
              fullWidth
              sx={{ py: 1.5, borderRadius: 2 }}
              onClick={onClickPayBulkSkip}
            >
              {isSubmiting ? 'Submiting' : 'Submit'}
            </Button>
          </Card>
        </Box>
      </Fade>
    </Modal>
  );
};

export default BillModal;
